import { KPI_NEUTRAL, RISK_LEVEL_COLORS } from "@app/theme/tokens";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BoltIcon from "@mui/icons-material/Bolt";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Box, Button, Skeleton, Tooltip } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import KpiCard from "../components/KpiCard";
import SpofFailureCard from "../components/SpofFailureCard";
import SpofMitigationsCard from "../components/SpofMitigationsCard";
import SpofRiskAssessmentCard from "../components/SpofRiskAssessmentCard";
import { useSpofCard } from "../hooks/useSpofRegister";
import PageHeader from "../layout/PageHeader";
import { mw, riskValue } from "../utils/format";
import { pidHref } from "../utils/links";

export default function SpofCardPage() {
  const { plantId, spofId } = useParams<{ plantId: string; spofId: string }>();
  const spof = useSpofCard(plantId!, spofId!).data;

  if (!spof) {
    return <Skeleton variant="rounded" height={520} />;
  }

  const link = spof.pid_link;

  return (
    <Box>
      <PageHeader
        title={spof.spof_id}
        trail={[
          { label: "Fleet overview", to: "/dashboard" },
          {
            label: spof.power_plant_name ?? "Plant",
            to: `/dashboard/plants/${spof.power_plant_id}`,
          },
          { label: spof.unit ?? spof.block ?? "SPOF" },
        ]}
        action={
          link ? (
            <Button
              variant="contained"
              component={RouterLink}
              to={pidHref(link)}
              endIcon={<OpenInNewIcon />}
            >
              Open on P&ID
            </Button>
          ) : (
            <Tooltip title="No P&ID occurrence matched this SPOF">
              <span>
                <Button
                  variant="contained"
                  disabled
                  endIcon={<OpenInNewIcon />}
                >
                  Open on P&ID
                </Button>
              </span>
            </Tooltip>
          )
        }
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(5, 1fr)",
            },
          }}
        >
          <KpiCard
            label="Equipment"
            value={spof.equipment}
            color={KPI_NEUTRAL}
            icon={PrecisionManufacturingIcon}
          />
          <KpiCard
            label="Power Loss"
            value={mw(spof.power_loss_mw)}
            suffix=" MW"
            color={KPI_NEUTRAL}
            icon={BoltIcon}
          />
          <KpiCard
            label="SPOF Owner"
            value={spof.spof_owner}
            color={KPI_NEUTRAL}
            icon={AccountTreeIcon}
          />
          <KpiCard
            label="Current Risk"
            value={riskValue(spof.risk_category, spof.final_risk_score)}
            color={RISK_LEVEL_COLORS[spof.risk_category ?? ""] ?? KPI_NEUTRAL}
            icon={GppMaybeIcon}
          />
          <KpiCard
            label="Residual Risk"
            value={riskValue(
              spof.residual_risk_category,
              spof.residual_risk_score,
            )}
            color={
              RISK_LEVEL_COLORS[spof.residual_risk_category ?? ""] ??
              KPI_NEUTRAL
            }
            icon={VerifiedUserIcon}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
          }}
        >
          <SpofFailureCard spof={spof} />
          <SpofRiskAssessmentCard spof={spof} />
        </Box>

        <SpofMitigationsCard spof={spof} />
      </Box>
    </Box>
  );
}
